import { DndContext, DragStartEvent } from '@dnd-kit/core';
import { ThirdPartyDraggable } from '@fullcalendar/interaction';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CollapsableHierarchyList,
  GoalCard,
  GoalSectionCard,
  LifeAreaCard,
  TaskCard,
} from 'shared/components/ui/collapsable-hierarchy-list';
import { OverlayComponentWrapper } from 'shared/components/ui/overlay-component-wrapper';
import { SortableOverlay } from 'shared/components/ui/sortable-overlay';
import { halfHour } from 'shared/constants';
import { useMouseOutside } from 'shared/hooks/use-mouse-outside';
import { useSortableSensors } from 'shared/hooks/use-sortable-sensors';
import { useUser } from 'shared/hooks/use-user';
import { ExtendedProps } from 'shared/lib/full-calendar/event-allow';
import { DataType } from 'shared/types/data-type';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { CalendarViewOptions } from 'shared/types/upcoming-view-options';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { noCollisionDetection } from 'shared/utils/no-collision-detection';

import { useAllTasksSections } from '../../hooks/use-all-tasks-sections';
import { useSelectedCalendarView } from '../../hooks/use-selected-calendar-view';
import { useToggleAllTasksSections } from '../../hooks/use-toggle-all-tasks-sections';

export const AllTasks: React.FC = () => {
  const listRef = useRef<HTMLUListElement>(null);
  const thirdPartyDraggableRef = useRef<ThirdPartyDraggable | null>(null);
  const selectedView = useSelectedCalendarView();

  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const onToggleSections = useToggleAllTasksSections();
  const { treeNodes, items, goals, lifeAreas, tasks } = useAllTasksSections();

  const [activeId, setActiveId] = useState<ID>();
  const handleDragStart = ({ active }: DragStartEvent) =>
    setActiveId(active.id.toString());
  const handleDragCancel = () => setActiveId(undefined);

  const sensors = useSortableSensors();

  const { activeItem, parent } = useMemo(() => {
    if (!activeId) {
      return {};
    }
    const supposedActiveItem = items.find(
      (item) =>
        item.id === activeId ||
        ('sections' in item &&
          !!item.sections?.find((section) => section.id === activeId)),
    );

    if (!supposedActiveItem) {
      return {};
    }
    if (supposedActiveItem.id !== activeId) {
      const parent = supposedActiveItem;
      return {
        parent,
        activeItem: (supposedActiveItem as Goal).sections?.find(
          (section) => section.id === activeId,
        ),
      };
    }
    return { activeItem: supposedActiveItem };
  }, [activeId, items]);

  const activeGoal = useMemo(
    () =>
      activeItem && 'tasks' in activeItem
        ? items.find(
            (item) =>
              'sections' in item &&
              item.sections?.find(({ id }) => id === activeItem.id),
          )
        : undefined,
    [activeItem, items],
  );

  useEffect(() => {
    if (!listRef.current) {
      return;
    }

    // Initialize ThirdPartyDraggable to connect @dnd-kit with Fullcalendar
    thirdPartyDraggableRef.current = new ThirdPartyDraggable(listRef.current, {
      // eslint-disable-next-line max-len -- long selector
      itemSelector: `[data-component-type="${DataType.Task}"], [data-component-type="${DataType.Goal}"], [data-component-type="${DataType.LifeArea}"]`,
      eventData: (el) => {
        const id = el.getAttribute('data-id');
        const task = tasks.find((task) => task.id === id);
        const title = el.getAttribute('data-title');
        const duration = el.getAttribute('data-duration') ?? halfHour;
        const componentType = el.getAttribute(
          'data-component-type',
        ) as DataType; // Cast to DataType

        if (!id) {
          return;
        }

        // Prepare extendedProps based on the component type
        const extendedProps: ExtendedProps = {
          weekStartsOn,
          task,
        };

        if (componentType === DataType.Goal) {
          extendedProps.goalId = id;
        } else if (componentType === DataType.Task) {
          extendedProps.taskId = id;
        } else if (componentType === DataType.LifeArea) {
          extendedProps.lifeAreaId = id;
        }

        const eventBase = {
          id,
          title,
          extendedProps,
          create: true,
        };

        if (selectedView !== CalendarViewOptions.Month) {
          return {
            ...eventBase,
            duration: { ms: duration },
          };
        }

        return eventBase;
      },
    });

    return () => {
      if (thirdPartyDraggableRef.current) {
        thirdPartyDraggableRef.current.destroy();
      }
    };
  }, [selectedView, tasks, weekStartsOn]);

  const isOutsideList = useMouseOutside(listRef, { enabled: !!activeId });

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragCancel}
      onDragCancel={handleDragCancel}
      sensors={sensors}
      collisionDetection={isOutsideList ? noCollisionDetection : undefined}
    >
      <CollapsableHierarchyList
        ref={listRef}
        treeNodes={treeNodes}
        tasks={tasks}
        allGoals={goals}
        allLifeAreas={lifeAreas}
        openedSections={user?.settings?.upcoming?.allTasks?.openedSections}
        onExpandedItemsChange={onToggleSections}
        preventDnd
        hasDnd
        hideTaskDurationInteraction
      />

      <SortableOverlay>
        {!!activeItem && (
          <OverlayComponentWrapper isOutside={isOutsideList}>
            {'visionPreview' in activeItem && (
              <LifeAreaCard lifeArea={activeItem} isGrabbed />
            )}
            {'sections' in activeItem && (
              <GoalCard
                goal={activeItem}
                lifeAreas={lifeAreas}
                goals={goals}
                isGrabbed
              />
            )}
            {'tasks' in activeItem && activeGoal && (
              <GoalSectionCard
                id={activeItem.id}
                goal={parent as Goal}
                isGrabbed
              />
            )}
            {'type' in activeItem && <TaskCard task={activeItem} />}
          </OverlayComponentWrapper>
        )}
      </SortableOverlay>
    </DndContext>
  );
};
